import { expectDefined } from '@utils';

import { LineIndex } from './LineIndex';
import { ParseError, ParseMessage } from './ParseError';
import type { Token } from './Token';

export class Scanner {
  text: string;
  lineIndex: LineIndex;
  errors: ParseMessage[];
  warnings: ParseMessage[];
  context: string[];
  comments: Token[];
  tokens: Token[];
  pos = 0;

  constructor(opts: {
    text: string;
    lineIndex?: LineIndex;
    errors?: ParseMessage[];
    warnings?: ParseMessage[];
    context?: string[];
    comments?: Token[];
    tokens?: Token[];
  }) {
    this.text = opts.text;
    this.errors = opts.errors ?? [];
    this.warnings = opts.warnings ?? [];
    this.lineIndex = opts.lineIndex ?? new LineIndex(opts.text);
    this.context = opts.context ?? [];
    this.comments = opts.comments ?? [];
    this.tokens = opts.tokens ?? [];
    this.pos = 0;
  }

  get hasErrors(): boolean {
    return this.errors.length > 0;
  }

  get hasWarnings(): boolean {
    return this.warnings.length > 0;
  }

  get hasMessages(): boolean {
    return this.hasErrors || this.hasWarnings;
  }

  get parseError(): ParseError | undefined {
    if (this.hasMessages) {
      return new ParseError(this.errors, this.warnings);
    }
    return undefined;
  }

  pushContext(name: string): void {
    this.context.push(name);
  }

  popContext(): string {
    const c = this.context.pop();
    if (c === undefined) {
      throw new Error('Context stack underflow');
    }
    return c;
  }

  withContext<T>(name: string, fn: () => T): T {
    this.pushContext(name);
    const result = fn.call(this);
    // If the above throws, then we won't pop the context.
    this.popContext();
    return result;
  }

  withSavedPosition<T>(fn: (commit: () => void) => T): T {
    const savedPos = this.pos;
    let committed = false;

    function commit() {
      committed = true;
    }
    try {
      return fn.call(this, commit);
    } finally {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!committed) {
        this.pos = savedPos;
      }
    }
  }

  error(message: string, pos: number): ParseMessage {
    const msg = new ParseMessage('Error', message, this.context, pos, this.lineIndex);
    this.errors.push(msg);
    // If we have too many errors, then we'll stop parsing.
    if (this.errors.length > 10) {
      throw new ParseError(this.errors, this.warnings);
    }
    return msg;
  }

  warning(message: string, pos: number): ParseMessage {
    const msg = new ParseMessage('Warning', message, this.context, pos, this.lineIndex);
    this.warnings.push(msg);
    return msg;
  }

  /**
   * Records a fatal error message and immediately throws a ParseError.
   * Use this for errors that should halt parsing immediately.
   * @param message - The error message text.
   * @param pos - The character position where the error occurred.
   * @returns This method never returns normally, it always throws.
   */
  fatalError(message: string, pos: number): never {
    // Create and record the ParseMessage like the regular error method
    const msg = new ParseMessage('Error', message, this.context, pos, this.lineIndex);
    this.errors.push(msg);
    // Immediately throw a ParseError containing all accumulated errors/warnings
    // Ensures the error type is consistent and includes context.
    throw expectDefined(this.parseError);
  }
  toJSON(opts?: { compact?: boolean }): object {
    const result: Record<string, unknown> = {
      text: this.text,
      lineIndex: this.lineIndex.toJSON(opts),
      errors: this.errors.map(e => e.toJSON(opts)),
      warnings: this.warnings.map(w => w.toJSON(opts)),
      tokens: this.tokens.map(t => t.toJSON(opts))
    };
    if (opts?.compact === false) {
      result.comments = this.comments.map(c => c.toJSON(opts));
      result.context = this.context;
      result.pos = this.pos;
    }
    return result;
  }
}
